"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TimePicker = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_icons_1 = require("@radix-ui/react-icons");
var react_1 = require("@stitches/react");
var react_2 = require("react");
var stitches_config_1 = require("../stitches.config");
var Dropdown_1 = require("./Dropdown");
var DropdownItem_1 = require("./DropdownItem");
var StyledTimePicker = (0, react_1.styled)("div", {
    display: "flex",
    border: "1px solid ".concat(stitches_config_1.theme.colors.gray7),
    boxShadow: stitches_config_1.theme.shadows.shadowInner,
    backgroundColor: stitches_config_1.theme.colors.white,
    paddingBlock: "6px",
    paddingInline: "10px",
    borderRadius: "6px",
    "&:hover": {
        border: "1px solid ".concat(stitches_config_1.theme.colors.gray8),
    },
    "&:focus-within": {
        border: "1px solid ".concat(stitches_config_1.theme.colors.blue9),
        boxShadow: "0px 0px 0px 2px ".concat(stitches_config_1.theme.colors.blue7),
    },
    variants: {
        fullWidth: {
            true: {
                width: "100%",
            },
        },
        error: {
            true: {
                borderColor: stitches_config_1.theme.colors.red7,
                "&:hover": {
                    borderColor: stitches_config_1.theme.colors.red8,
                    background: stitches_config_1.theme.colors.red2,
                },
                "&:focus": {
                    borderColor: stitches_config_1.theme.colors.red9,
                    boxShadowColor: stitches_config_1.theme.colors.error,
                    outline: "none",
                },
            },
            false: {
                borderColor: stitches_config_1.theme.colors.gray7,
                "&:hover": {
                    borderColor: stitches_config_1.theme.colors.gray8,
                },
                "&:focus": {
                    borderColor: stitches_config_1.theme.colors.blue9,
                    boxShadowColor: stitches_config_1.theme.colors.focus,
                    outline: "none",
                },
            },
        },
    },
});
var InputContainer = (0, react_1.styled)("div", {
    display: "flex",
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: "2px",
});
var StyledInput = (0, react_1.styled)("input", {
    width: "100%",
    border: "none",
    outline: "none",
    variants: {
        fullWidth: {
            true: {
                flex: 1,
            },
        },
    },
});
var dateFormatter = Intl.DateTimeFormat(undefined, {
    timeStyle: "short",
});
function timeToMinutes(time) {
    // needs time in hh:mm format
    var hours = parseInt(time.split(":")[0]);
    var minutes = time.split(":")[1] ? parseInt(time.split(":")[1]) : 0;
    return hours * 60 + minutes;
}
function minutesToTime(minutes) {
    var date = new Date("December 14, 2026 00:00");
    date.setHours(Math.floor(minutes / 60));
    date.setMinutes(minutes % 60);
    return dateFormatter.format(date);
}
function parseTimeInput(input) {
    var timeNumber = parseInt(input.split(":").join(""));
    var hours = 0;
    var minutes = 0;
    if (isNaN(timeNumber)) {
        return undefined;
    }
    function handleHours(timeNumber) {
        if (timeNumber < 12 && !input.toLowerCase().includes("am")) {
            hours += 12;
        }
        if (timeNumber <= 24) {
            hours += timeNumber;
        }
        else {
            return undefined;
        }
        return true;
    }
    function handleMinutes(timeNumber) {
        if (timeNumber > 59) {
            return undefined;
        }
        else {
            minutes = timeNumber;
            return true;
        }
    }
    if (timeNumber.toString().length <= 2) {
        if (!handleHours(timeNumber)) {
            return undefined;
        }
    }
    if (timeNumber.toString().length === 3) {
        if (!handleMinutes(parseInt(timeNumber.toString()[1] + timeNumber.toString()[2]))) {
            return undefined;
        }
        if (!handleHours(parseInt(timeNumber.toString()[0]))) {
            return undefined;
        }
    }
    if (timeNumber.toString().length === 4) {
        if (!handleMinutes(parseInt(timeNumber.toString()[2] + timeNumber.toString()[3]))) {
            return undefined;
        }
        if (!handleHours(parseInt(timeNumber.toString()[0] + timeNumber.toString()[1]))) {
            return undefined;
        }
    }
    if (timeNumber.toString().length > 4) {
        return undefined;
    }
    return { hours: hours, minutes: minutes };
}
var ItemsWrapper = (0, react_1.styled)("div", {
    maxHeight: "200px",
    overflow: "auto",
});
function TimePicker(props) {
    var _a;
    var ref = (0, react_2.useRef)(null);
    var _b = (0, react_2.useState)(props.value), currentValue = _b[0], setCurrentValue = _b[1];
    var currentDate = new Date("December 14, 2026 ".concat((_a = props.value) !== null && _a !== void 0 ? _a : "00:00"));
    var _c = (0, react_2.useState)(false), typing = _c[0], setTyping = _c[1];
    var value = props.value, otherProps = __rest(props, ["value"]);
    function generateDropdownTimes() {
        var _a;
        var returnValue = [];
        var maxTime = props.maxTime ? timeToMinutes(props.maxTime) : 1440;
        var _loop_1 = function (i) {
            if (!props.minTime || i >= timeToMinutes(props.minTime)) {
                returnValue.push((0, jsx_runtime_1.jsx)(DropdownItem_1.DropdownItem, { text: minutesToTime(i), onClick: function () {
                        currentDate.setHours(Math.floor(i / 60));
                        currentDate.setMinutes(i % 60);
                        props.onChange &&
                            props.onChange("".concat(currentDate.getHours(), ":").concat(currentDate.getMinutes()));
                        setCurrentValue("".concat(currentDate.getHours(), ":").concat(currentDate.getMinutes()));
                    } }));
            }
        };
        for (var i = 0; i < maxTime; i += (_a = props.timeStep) !== null && _a !== void 0 ? _a : 30) {
            _loop_1(i);
        }
        return returnValue;
    }
    return ((0, jsx_runtime_1.jsx)(Dropdown_1.Dropdown, __assign({ button: (0, jsx_runtime_1.jsx)(StyledTimePicker, __assign({ fullWidth: props.fullWidth }, { children: (0, jsx_runtime_1.jsxs)(InputContainer, { children: [(0, jsx_runtime_1.jsx)(react_icons_1.ClockIcon, {}), (0, jsx_runtime_1.jsx)(StyledInput, __assign({}, otherProps, { disabled: props.disabled, ref: ref, type: "text", value: typing
                            ? currentValue
                            : value
                                ? dateFormatter.format(currentDate)
                                : "--:--", onChange: function (e) {
                            setTyping(true);
                            setCurrentValue(e.target.value);
                        }, onBlur: function () {
                            setTyping(false);
                            if (props.onChange &&
                                currentValue &&
                                parseTimeInput(currentValue)) {
                                currentDate.setHours(parseTimeInput(currentValue).hours);
                                currentDate.setMinutes(parseTimeInput(currentValue).minutes);
                                props.onChange("".concat(currentDate.getHours(), ":").concat(currentDate.getMinutes()));
                                setCurrentValue("".concat(currentDate.getHours(), ":").concat(currentDate.getMinutes()));
                            }
                            else if (props.onChange && !currentValue) {
                                setCurrentValue("00:00");
                                props.onChange("");
                            }
                        }, placeholder: props.placeholder, fullWidth: props.fullWidth }))] }) })) }, { children: (0, jsx_runtime_1.jsx)(ItemsWrapper, { children: generateDropdownTimes() }) })));
}
exports.TimePicker = TimePicker;
//# sourceMappingURL=TimePicker.js.map